import React, { useState, useEffect } from 'react';
import { EmployeeManagementEnhanced } from './employees/EmployeeManagementEnhanced';
import { SchedulingDashboard } from './scheduling/SchedulingDashboard';
import { JobManagement } from './scheduling/JobManagement';
import { LaborRulesDashboard } from './laborRules/LaborRulesDashboard';
import { OvertimeAnalytics } from './reports/OvertimeAnalytics';
import { getUserRole, hasPermission } from '../utils/roleUtils';
import { useAuth } from '../contexts/AuthContext';
import { useScope } from '../contexts/ScopeContext';
import { API_ENDPOINTS } from '../config/apiRouting';
import authenticatedFetch from '../utils/authenticatedFetch';
import './PeopleManagement.css';

interface PeopleManagementProps {
  enterpriseId: string;
  storeId?: string;
  user?: any;
  currentUser?: {
    id: string;
    role: string;
    enterpriseId: string;
  };
}

type ActiveTab = 'employees' | 'schedules' | 'jobs' | 'labor-rules' | 'reports';

export const PeopleManagement: React.FC<PeopleManagementProps> = ({ 
  enterpriseId: initialEnterpriseId, 
  storeId: initialStoreId,
  user,
  currentUser 
}) => {
  const { enterpriseId: scopeEnterpriseId, storeId: scopeStoreId } = useScope();
  const [activeTab, setActiveTab] = useState<ActiveTab>('employees');
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState<string>(
    initialEnterpriseId || scopeEnterpriseId || ''
  );
  const [selectedStoreId, setSelectedStoreId] = useState<string>(
    initialStoreId || scopeStoreId || ''
  );
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const { session } = useAuth();

  // Track whether the user has manually picked an enterprise inside this
  // screen. If they haven't, follow the header scope selector. Once they
  // pick locally, we stop auto-syncing so their picker choice sticks.
  const [userPickedEnterprise, setUserPickedEnterprise] = useState(false);
  const [userPickedStore, setUserPickedStore] = useState(false);

  useEffect(() => {
    if (!userPickedEnterprise && scopeEnterpriseId && scopeEnterpriseId !== selectedEnterpriseId) {
      setSelectedEnterpriseId(scopeEnterpriseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEnterpriseId, userPickedEnterprise]);

  useEffect(() => {
    if (!userPickedStore && scopeStoreId && scopeStoreId !== selectedStoreId) {
      setSelectedStoreId(scopeStoreId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeStoreId, userPickedStore]);
  
  // Use centralized role management - prefer user from auth context over currentUser prop
  const actualUser = user || currentUser;
  const userRole = getUserRole(actualUser, session);

  useEffect(() => {
    fetchEnterprises();
  }, []);

  useEffect(() => {
    if (selectedEnterpriseId) {
      fetchStores();
    }
  }, [selectedEnterpriseId]);

  const fetchEnterprises = async () => {
    try {
      console.log('[PeopleManagement] Fetching enterprises...');
      const response = await authenticatedFetch(API_ENDPOINTS.enterprises());
      console.log('[PeopleManagement] Enterprises response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[PeopleManagement] Enterprises data:', data);
        // Handle both array response and {data: []} wrapped response
        const enterpriseList = Array.isArray(data) ? data : (data.data || data.enterprises || []);
        setEnterprises(enterpriseList);
      } else {
        console.error('[PeopleManagement] Failed to fetch enterprises:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('[PeopleManagement] Error fetching enterprises:', error);
    }
  };

  const fetchStores = async () => {
    try {
      console.log('[PeopleManagement] Fetching stores for enterprise:', selectedEnterpriseId);
      const response = await authenticatedFetch(API_ENDPOINTS.enterpriseStores(selectedEnterpriseId));
      console.log('[PeopleManagement] Stores response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[PeopleManagement] Stores data:', data);
        const allStores = Array.isArray(data) ? data : (data.data || data.stores || []);
        setStores(allStores);
      } else {
        console.error('[PeopleManagement] Failed to fetch stores:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('[PeopleManagement] Error fetching stores:', error);
    }
  };


  const tabs = [
    {
      id: 'employees' as const,
      label: 'Employees',
      description: 'Manage employee information and positions'
    },
    {
      id: 'schedules' as const,
      label: 'Scheduling',
      description: 'Create and manage employee schedules'
    },
    {
      id: 'jobs' as const,
      label: 'Jobs',
      description: 'Define job positions and pay structures'
    },
    {
      id: 'labor-rules' as const,
      label: 'Labor Rules',
      description: 'Configure labor law compliance rules'
    },
    {
      id: 'reports' as const,
      label: 'Reports',
      description: 'Overtime analytics and compliance reports'
    }
  ];

  const canAccessTab = (tabId: ActiveTab) => {
    if (!actualUser) return true; // Allow access if no user context
    
    // Use centralized permission system for consistency
    switch (tabId) {
      case 'employees':
        // Employees: Can manage users (admin+ roles) or has specific employee management permission
        return hasPermission(actualUser, 'canManageUsers', session) || userRole === 'manager';
      case 'schedules':
        // Schedules: Similar to employees, manager level access
        return hasPermission(actualUser, 'canManageUsers', session) || userRole === 'manager';
      case 'jobs':
        // Jobs: Manager level access to define positions
        return hasPermission(actualUser, 'canManageUsers', session) || userRole === 'manager';
      case 'labor-rules':
        // Labor rules: Admin level only (higher privilege)
        return hasPermission(actualUser, 'canManageRoles', session);
      case 'reports':
        // Reports: View reports permission
        return hasPermission(actualUser, 'canViewReports', session);
      default:
        return false;
    }
  };

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'employees':
        return (
          <EmployeeManagementEnhanced 
            enterpriseId={selectedEnterpriseId}
            storeId={selectedStoreId}
            user={user}
          />
        );
      case 'schedules':
        return selectedStoreId ? (
          <SchedulingDashboard 
            storeId={selectedStoreId} 
            enterpriseId={selectedEnterpriseId}
          />
        ) : (
          <div className="no-store-selected">
            <h3>Store Required</h3>
            <p>Please select a store to manage schedules.</p>
          </div>
        );
      case 'jobs':
        return (
          <JobManagement 
            enterpriseId={selectedEnterpriseId}
            storeId={selectedStoreId || ''}
          />
        );
      case 'labor-rules':
        return (
          <LaborRulesDashboard 
            enterpriseId={selectedEnterpriseId}
            storeId={selectedStoreId}
          />
        );
      case 'reports':
        return (
          <OvertimeAnalytics 
            enterpriseId={selectedEnterpriseId}
            storeId={selectedStoreId}
          />
        );
      default:
        return <div>Select a tab to get started</div>;
    }
  };

  return (
    <div className="people-management">
      {/* Enterprise + store selection live in the header strip — no in-page pickers. */}

      <nav className="people-navigation">
        <div className="nav-tabs">
          {tabs.map((tab) => {
            const canAccess = canAccessTab(tab.id);
            return (
              <button
                key={tab.id}
                className={`nav-tab ${activeTab === tab.id ? 'active' : ''} ${!canAccess ? 'disabled' : ''}`}
                onClick={() => canAccess && setActiveTab(tab.id)}
                disabled={!canAccess}
                title={!canAccess ? 'Insufficient permissions' : tab.description}
              >
                <span className="tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="people-content">
        {canAccessTab(activeTab) ? (
          renderActiveComponent()
        ) : (
          <div className="access-denied">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this section.</p>
          </div>
        )}
      </div>

      <div className="people-footer">
        <div className="footer-info">
          <span>People Management System v1.0</span>
          <span>•</span>
          <span>Labor Law Compliant</span>
          <span>•</span>
          <span>Multi-Location Support</span>
        </div>
      </div>
    </div>
  );
};